import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, InputAdornment, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import { CodeText, ScopeLevelChip, StatusChip } from './OntologyBrowser';
import {
    KIND_LABEL, OntologyKind, categoryForConceptType, conceptTypeLabel, isLinkableCode,
} from './ontologyFormat';
import { normalizeSearch, ontologyErrorMessage, searchOntology } from '../../api/ontologyApi';
import { dsVar } from '../../theme/tokens';

/**
 * One box across all three collections.
 *
 * The three tabs ask the user to know, before they type, whether "structuring" is stored
 * as a term, named in a policy or bound by a threshold. This screen removes that question:
 * results carry their own kind and the facets narrow AFTER the answer, not before it.
 *
 * Not a browse surface — the API caps results per kind and does not page. When a kind has
 * more than is shown, the footer link hands the query to that kind's own listing, which is
 * where paging and the full filter set live.
 */

const KIND_ORDER: OntologyKind[] = ['concepts', 'policies', 'parameters'];

interface SearchResult {
    kind: OntologyKind;
    code: string;
    label: string;
    snippet?: string | null;
    concept_type?: string;
    scope_level?: string;
    status?: string;
}

/** What a result is called in the facet list and on its badge. */
const kindBadge = (result: SearchResult): string => {
    if (result.kind !== 'concepts') return KIND_LABEL[result.kind];
    // A concept says which category it is — "Red Flag" is far more use than "Risk Library".
    const category = categoryForConceptType(result.concept_type);
    return category ? category.label.replace(/s$/, '') : conceptTypeLabel(result.concept_type);
};

const OntologySearchPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const query = searchParams.get('q') ?? '';
    const kindFacet = searchParams.get('kind') ?? '';

    const [input, setInput] = useState(query);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { setInput(query); }, [query]);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            setCounts({});
            return undefined;
        }

        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await searchOntology(query, { signal: controller.signal });
                if (!active) return;
                const normalized = normalizeSearch(res);
                setResults(normalized.results as SearchResult[]);
                setCounts(normalized.counts);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setError(ontologyErrorMessage(err, 'Could not run that search.'));
                setResults([]);
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [query]);

    const visible = useMemo(
        () => (kindFacet ? results.filter((r) => r.kind === kindFacet) : results),
        [results, kindFacet],
    );

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        setSearchParams({ q: input.trim() });
    };

    const setKindFacet = (kind: string) => {
        const next = new URLSearchParams(searchParams);
        if (kind) next.set('kind', kind);
        else next.delete('kind');
        setSearchParams(next);
    };

    const openResult = (result: SearchResult) => {
        if (!isLinkableCode(result.code)) return;
        navigate(`/ontology/${result.kind}/${encodeURIComponent(result.code)}`);
    };

    const shownFor = (kind: OntologyKind) => results.filter((r) => r.kind === kind).length;

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            <Sidebar activePage="ontology" />

            <div className="flex flex-1 flex-col overflow-hidden">
                <AppHeader
                    left={(
                        <Button
                            onClick={() => navigate('/ontology')}
                            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                            sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.875rem' }}
                        >
                            Policy &amp; Risk Library
                        </Button>
                    )}
                />

                <main className="flex-1 overflow-y-auto px-8 pb-16">
                    <Box sx={{ maxWidth: '60rem', pt: 4 }}>
                        <Box component="form" onSubmit={submit} sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                autoFocus
                                placeholder="Search terms, policies and thresholds"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 20, color: dsVar('fg-muted') }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: dsVar('surface'),
                                        color: dsVar('fg-body'),
                                        fontSize: '0.9375rem',
                                        '& fieldset': { borderColor: dsVar('line') },
                                        '&:hover fieldset': { borderColor: dsVar('line') },
                                        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                                    },
                                }}
                            />
                        </Box>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {/* Facets narrow an answer the user can already see, rather than
                            asking them to pick a collection before searching. */}
                        {query.trim() !== '' && !loading && results.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
                                {[{ value: '', label: 'Everything', count: results.length }]
                                    .concat(KIND_ORDER
                                        .filter((kind) => shownFor(kind) > 0)
                                        .map((kind) => ({
                                            value: kind,
                                            label: KIND_LABEL[kind],
                                            count: shownFor(kind),
                                        })))
                                    .map((facet) => {
                                        const isActive = facet.value === kindFacet;
                                        return (
                                            <Box
                                                key={facet.value || 'all'}
                                                component="button"
                                                type="button"
                                                onClick={() => setKindFacet(facet.value)}
                                                sx={{
                                                    px: 1.5, py: 0.6,
                                                    border: '1px solid',
                                                    borderColor: isActive ? dsVar('accent-fg') : dsVar('line'),
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontFamily: '"Inter", sans-serif',
                                                    fontSize: '0.8125rem',
                                                    color: isActive ? dsVar('accent-fg') : dsVar('fg-2'),
                                                    backgroundColor: isActive ? 'rgba(255,222,89,0.08)' : 'transparent',
                                                }}
                                            >
                                                {facet.label} ({facet.count})
                                            </Box>
                                        );
                                    })}
                            </Box>
                        )}

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                                <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                            </Box>
                        )}

                        {!loading && query.trim() !== '' && visible.length === 0 && !error && (
                            <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), pt: 4 }}>
                                Nothing matches “{query}”.
                            </Typography>
                        )}

                        {!loading && visible.map((result) => (
                            <Box
                                key={`${result.kind}-${result.code}`}
                                component="button"
                                type="button"
                                onClick={() => openResult(result)}
                                sx={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    p: 2, mb: 1.25,
                                    border: `1px solid ${dsVar('line')}`, borderRadius: '8px',
                                    backgroundColor: dsVar('surface'), cursor: 'pointer',
                                    fontFamily: '"Inter", sans-serif',
                                    '&:hover': { borderColor: 'rgba(255,222,89,0.5)' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.06em',
                                            textTransform: 'uppercase', color: dsVar('fg-muted'),
                                        }}
                                    >
                                        {kindBadge(result)}
                                    </Typography>
                                    <ScopeLevelChip scopeLevel={result.scope_level} />
                                    <StatusChip status={result.status} />
                                </Box>

                                <Typography sx={{ fontSize: '0.9375rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                                    {result.label}
                                </Typography>
                                <CodeText>{result.code}</CodeText>

                                {result.snippet && (
                                    <Typography
                                        sx={{
                                            fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.55, mt: 0.75,
                                            display: '-webkit-box', WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}
                                    >
                                        {result.snippet}
                                    </Typography>
                                )}
                            </Box>
                        ))}

                        {/* Results are capped per kind, so say so and hand the query to the
                            listing that can actually page through the rest. */}
                        {!loading && KIND_ORDER.map((kind) => {
                            const total = counts[kind] ?? 0;
                            const shown = shownFor(kind);
                            if (total <= shown) return null;
                            if (kindFacet && kindFacet !== kind) return null;
                            return (
                                <Button
                                    key={kind}
                                    onClick={() => navigate(
                                        `/ontology/${kind}?q=${encodeURIComponent(query)}`)}
                                    sx={{ textTransform: 'none', color: dsVar('accent-fg'), fontSize: '0.8125rem', display: 'block' }}
                                >
                                    See all {total} matches in {KIND_LABEL[kind]} →
                                </Button>
                            );
                        })}
                    </Box>
                </main>
            </div>
        </div>
    );
};

export default OntologySearchPage;
